import { SITE_SECTION, SUB_SECTION } from 'src/app/shared/constants/tealium';
import { getProductCategories } from 'src/app/utils/analytics/utils/getProductCategories';
import { getProductQuantities } from 'src/app/utils/analytics/utils/getProductQuantities';
import {
  PRODUCT_EVENTS_PURCHASE,
  TEALIUM_PAGE_CLASSIFY,
  TEALIUM_PAGE_CONCIERGE,
  TEALIUM_PAGE_EDIT_PAY_AND_POST,
  TEALIUM_PAGE_EDIT,
  TEALIUM_PAGE_GUARANTEED_HIRE,
  TEALIUM_PAGE_PAY_AND_POST,
  TEALIUM_PAGE_REFRESH,
  TEALIUM_PAGE_ROLE_REQS,
  TEALIUM_PAGE_SELECT_AD,
  TEALIUM_PAGE_SUCCESS,
  TEALIUM_PAGE_WRITE,
  TEALIUM_PAGE_MULTI_LOCATION,
} from 'src/app/v1/analytics';
import type { Tealium } from 'src/types/appState';

import { getProductIds } from '../getProductIds';
import { getProductPurchaseId } from '../getProductPurchaseId';
import { getProductTotalPrice } from '../getProductTotalPrice';

import type {
  EditOrRefreshPageUtagData,
  EditPageUtagData,
  EditPayAndPostPageUtagData,
  PageUtagData,
  RefreshPageUtagData,
  ViewEventData,
} from './type';

export const getViewEventDataFromState = (tealium: Tealium): ViewEventData => {
  const {
    advertiser,
    cajaFlowType,
    draftId,
    hirerContract,
    hirerInfo,
    isLinkOutApplyViaSeek,
    jobAdTypeSelected,
    page,
    seekAdvertiserId,
    seekListingId,
    sourceJobId,
    userId,
    testingId,
  } = tealium;

  const basicUtagData: PageUtagData = {
    advertiserTypeHirer: advertiser?.type,
    advertiserTypePricing: advertiser?.pricingType,
    cajaFlowType,
    draftId,
    hirerContractAdType: hirerContract?.adType,
    hirerContractBalance: hirerContract?.balance,
    hirerContractType: hirerContract?.type,
    hirerContractValue: hirerContract?.value,
    hirerUserEmail: hirerInfo?.emailAddress ?? '',
    hirerUserFullName: `${hirerInfo?.firstName} ${hirerInfo?.lastName}`,
    page,
    seekAdvertiserId,
    siteSection: SITE_SECTION,
    subSection: SUB_SECTION,
    userId,
    testingId,
  };

  const basicEditOrRefreshData: EditOrRefreshPageUtagData = {
    ...basicUtagData,
    country: tealium.country,
    seekListingId,
  };

  const productTotalPrice = getProductTotalPrice(
    tealium.adBudgetPurchased,
    tealium.productTotalPrice,
    tealium.adBudgetValue,
  );
  const productCategories = getProductCategories(tealium.adBudgetPurchased);
  const purchaseId = cajaFlowType === 'refresh' ? seekListingId : draftId;

  switch (page) {
    // Create Flow
    case TEALIUM_PAGE_CLASSIFY:
      return {
        ...basicUtagData,
        cajaSalaryCurrency: tealium.cajaSalaryCurrency,
        jobAdTypeSelected: Boolean(jobAdTypeSelected)
          ? jobAdTypeSelected
          : undefined,
        experiments: tealium.experiments,
      };
    case TEALIUM_PAGE_CONCIERGE:
    case TEALIUM_PAGE_GUARANTEED_HIRE:
      return {
        ...basicUtagData,
        cajaPayType: tealium.cajaPayType,
        cajaSalaryCurrency: tealium.cajaSalaryCurrency,
        cajaWorkType: tealium.cajaWorkType,
        country: tealium.country,
        jobAdClassification: tealium.jobAdClassification,
        jobAdSubClassification: tealium.jobAdSubClassification,
        jobAdTypeSelected,
        roleTitle: tealium.roleTitle,
        salaryRangeSelected: tealium.salaryRangeSelected,
      };
    case TEALIUM_PAGE_MULTI_LOCATION:
      return {
        ...basicUtagData,
        cajaPayType: tealium.cajaPayType,
        cajaSalaryCurrency: tealium.cajaSalaryCurrency,
        cajaWorkType: tealium.cajaWorkType,
        country: tealium.country,
        jobAdClassification: tealium.jobAdClassification,
        jobAdSubClassification: tealium.jobAdSubClassification,
        jobAdTypeSelected,
        roleTitle: tealium.roleTitle,
        salaryRangeSelected: tealium.salaryRangeSelected,
        jobId: sourceJobId,
      };
    case TEALIUM_PAGE_WRITE:
      return {
        ...basicUtagData,
        jobAdTypeSelected,
      };
    case TEALIUM_PAGE_SELECT_AD:
      return {
        ...basicUtagData,
        priceHidden: tealium.priceVisibility === 'hidden',
        productTransactionId: [Number(draftId) ?? 0],
      };
    case TEALIUM_PAGE_ROLE_REQS:
      return {
        ...basicUtagData,
        isLinkOutApplyViaSeek,
        jobAdTypeSelected,
      };
    case TEALIUM_PAGE_PAY_AND_POST:
      return {
        ...basicUtagData,
        jobAdTypeSelected,
        priceHidden: tealium.priceVisibility === 'hidden',
        productTransactionId: [Number(draftId) ?? 0],
      };
    case TEALIUM_PAGE_SUCCESS:
      return {
        ...basicUtagData,
        autoClassifyCategoryChosen: tealium.autoClassifyCategoryChosen,
        autoClassifyCategorySuggested: tealium.autoClassifyCategorySuggested,
        cajaPayType: tealium.cajaPayType,
        cajaSalaryCurrency: tealium.cajaSalaryCurrency,
        cajaWorkType: tealium.cajaWorkType,
        country: tealium.country,
        finalJobSummaryCharacterCount: tealium.finalJobSummaryCharacterCount,
        hasCoverImage: tealium.hasCoverImage,
        hasLogo: tealium.hasLogo,
        isLinkOutApplyViaSeek: tealium.isLinkOutApplyViaSeek,
        isPrivateAd: tealium.isPrivateAd,
        isSalaryHidden: !tealium.displayPay,
        jobAdClassification: tealium.jobAdClassification,
        jobAdSubClassification: tealium.jobAdSubClassification,
        jobAdTypePosted: tealium.jobAdTypePosted,
        jobAdTypeSelected: tealium.jobAdTypeSelected,
        numberOfBenefits: tealium.numberOfBenefits,
        numberOfQualifications: tealium.numberOfQualifications,
        numberOfTasks: tealium.numberOfTasks,
        paymentMethod: tealium.paymentMethod,
        priceHidden: tealium.priceVisibility === 'hidden',
        productCategories,
        productEvents: [PRODUCT_EVENTS_PURCHASE],
        productIds: getProductIds(
          tealium.jobAdTypeSelected,
          tealium.jobAdTypePosted,
          tealium.adBudgetPurchased,
        ),
        productPurchaseId: getProductPurchaseId(purchaseId),
        productQuantities: getProductQuantities(tealium.adBudgetPurchased),
        productSetId: tealium.productSetId,
        productTotalPrice,
        productTransactionId: purchaseId,
        recommendedSalaryRange: tealium.recommendedSalaryRange,
        roleTitle: tealium.roleTitle,
        rrNumberOfQuestionsPosted: tealium.rrNumberOfQuestions,
        rrQuestionIdsPosted: tealium.rrQuestionIds,
        salaryRangeSelected: tealium.salaryRangeSelected,
        seekListingId,
        experiments: tealium.experiments,
      };

    // Edit Flow
    case TEALIUM_PAGE_EDIT: {
      return basicEditOrRefreshData as EditPageUtagData;
    }

    case TEALIUM_PAGE_EDIT_PAY_AND_POST: {
      return {
        ...basicEditOrRefreshData,
        hasCoverImage: tealium.hasCoverImage,
        hasLogo: tealium.hasLogo,
        isLinkOutApplyViaSeek: tealium.isLinkOutApplyViaSeek,
        jobAdTypeSelected: tealium.jobAdTypeSelected,
        jobStatus: 'open',
        productCategories,
        productEvents: [PRODUCT_EVENTS_PURCHASE],
        productTotalPrice,
      } as EditPayAndPostPageUtagData;
    }

    // Refresh Flow
    case TEALIUM_PAGE_REFRESH: {
      return {
        ...basicEditOrRefreshData,
        jobAdClassification: tealium.jobAdClassification,
        jobAdSubClassification: tealium.jobAdSubClassification,
        jobStatus: 'open',
        priceVisibility: tealium.priceVisibility,
        productIds: getProductIds(
          tealium.jobAdTypeSelected,
          tealium.jobAdTypePosted,
          tealium.adBudgetPurchased,
        ),
        productPurchaseId: getProductPurchaseId(purchaseId),
        productQuantities: getProductQuantities(tealium.adBudgetPurchased),
        productTotalPrice,
        productTransactionId: getProductPurchaseId(purchaseId),
      } as RefreshPageUtagData;
    }

    default:
      return basicUtagData;
  }
};
